// Smooth, custom-eased scrolling shared by the customiser accordion.
//
// Native scrollIntoView({ behavior: "smooth" }) tops out around 400ms and
// computes the target position once. Our accordion collapses/expands a
// section underneath the scroll while it runs, so the target keeps
// moving. easedScrollTo recomputes the target every frame so the page
// lands accurately on the newly-opened step instead of near it.

import * as React from "react";

// Nearest scrollable ancestor of `el`. On a PDP that's nothing (null =
// scroll the window). Inside the in-cart modal it's `.rkc-cz__host`
// (overflow-y: auto), so accordion advances must scroll the modal, not
// the page behind it.
export function findScrollContainer(el: HTMLElement | null): HTMLElement | null {
  if (!el || typeof window === "undefined") return null;
  let node: HTMLElement | null = el.parentElement;
  while (node && node !== document.body && node !== document.documentElement) {
    const oy = window.getComputedStyle(node).overflowY;
    if (
      (oy === "auto" || oy === "scroll") &&
      node.scrollHeight > node.clientHeight + 1
    ) {
      return node;
    }
    node = node.parentElement;
  }
  return null;
}

// 2026-07-10 (Oscar, mobile): typing with the on-screen keyboard open must
// never be interrupted by a follow-scroll - the viewport is already being
// managed by the keyboard and a competing scroll reads as a "weird jump".
function editableHasFocus(): boolean {
  const a = typeof document !== "undefined" ? document.activeElement : null;
  if (!a) return false;
  const tag = a.tagName;
  return (
    tag === "TEXTAREA" ||
    (tag === "INPUT" &&
      !/^(checkbox|radio|button|submit|range)$/i.test(
        (a as HTMLInputElement).type,
      ))
  );
}

// ── 2026-07-24 mobile scroll policy (Oscar: "it keeps scrolling all around
// automatically, but almost never to the correct place, so it will scroll out
// from underneath you, then land somewhere wrong") ─────────────────────────
//
// The editableHasFocus() check alone is defeated on iOS by ordering: the tap
// that triggers a scroll blurs the input FIRST, so focus is already gone when
// we check, and then the keyboard-close reflow shifts scrollY underneath the
// animation. Track the last blur and refuse to start inside its window.
let lastEditableBlurAt = 0;
// A finger on the glass means the page belongs to the customer. touchend
// starts a short grace so a scroll cannot start against iOS momentum, which
// emits no wheel/touchstart events after the finger lifts.
let touchActive = false;
let lastTouchEndAt = 0;
// Whether the current touch sequence MOVED (a drag/flick). A clean tap
// never arms the momentum grace below - taps cannot create momentum, and
// swallowing tap-initiated scrolls broke the sticky bar's "Continue
// designing" jump on real phones (every click lands within ~50ms of its
// own touchend). Found in the 2026-07-24 mobile audit.
let touchMoved = false;
// One shared generation counter: starting any new eased scroll, or any user
// input, invalidates every loop still in flight. Before this, two concurrent
// loops each held a private aborted flag and interleaved absolute
// window.scrollTo writes, which is exactly "lands somewhere wrong".
let scrollGeneration = 0;

if (typeof window !== "undefined") {
  const opts: AddEventListenerOptions = { passive: true, capture: true };
  document.addEventListener(
    "focusout",
    (e) => {
      const t = e.target as HTMLElement | null;
      if (!t) return;
      const tag = t.tagName;
      if (
        tag === "TEXTAREA" ||
        (tag === "INPUT" &&
          !/^(checkbox|radio|button|submit|range)$/i.test(
            (t as HTMLInputElement).type,
          ))
      ) {
        lastEditableBlurAt = performance.now();
      }
    },
    opts,
  );
  window.addEventListener(
    "touchstart",
    () => {
      touchActive = true;
      touchMoved = false;
      scrollGeneration++;
    },
    opts,
  );
  window.addEventListener(
    "touchmove",
    () => {
      touchMoved = true;
    },
    opts,
  );
  window.addEventListener(
    "touchend",
    () => {
      touchActive = false;
      // Only a moved finger can be mid-momentum; a tap leaves the grace
      // unarmed so the click it produces may scroll immediately.
      if (touchMoved) lastTouchEndAt = performance.now();
    },
    opts,
  );
  window.addEventListener("wheel", () => scrollGeneration++, opts);
  window.addEventListener("keydown", () => scrollGeneration++, opts);
}

/** The keyboard-close reflow window. No programmatic scroll may start within
 *  it: iOS is still resettling the viewport and any absolute write lands
 *  wrong. */
const BLUR_WINDOW_MS = 600;
/** After the finger lifts, momentum may still be running (it emits no
 *  events). Starting a scroll inside this grace fights it. */
const MOMENTUM_GRACE_MS = 150;

/** True when `el` already sits comfortably within the viewport band between
 *  the sticky header area and the mobile sticky bar. A scroll to something
 *  the customer can already see is pure motion with no information. */
/** Height of whatever sticky/fixed chrome is pinned to the top of the
 *  viewport, measured from the page rather than assumed.
 *
 *  2026-07-25: the band below hardcoded 72px, but the storefront theme's
 *  sticky header is 162.5px on a phone. Anything sitting under it counted
 *  as "comfortably visible", so easedScrollTo returned without scrolling
 *  and the mobile bar's "Continue designing" was a dead tap - the customer
 *  pressed it and nothing moved, because the thing it wanted to show them
 *  was behind the header.
 *
 *  Measured once per resize: this walks the DOM, so it must not run on
 *  every scroll request. The customiser's own overlays are skipped - they
 *  are what we are usually scrolling to. */
let cachedHeaderBottom: number | null = null;
let cachedHeaderWidth = -1;
function stickyHeaderBottom(): number {
  const w = window.innerWidth || 0;
  if (cachedHeaderBottom !== null && cachedHeaderWidth === w) {
    return cachedHeaderBottom;
  }
  let bottom = 0;
  try {
    const nodes = document.body.querySelectorAll<HTMLElement>(
      "header, [class*='header'], [data-sticky], [class*='sticky']",
    );
    for (const el of Array.from(nodes).slice(0, 40)) {
      // Never measure our own UI.
      if (el.closest(".rk-pc-root, .rk-pc-mobile-bar, .rk-pc-lead-overlay")) {
        continue;
      }
      const cs = window.getComputedStyle(el);
      if (cs.position !== "sticky" && cs.position !== "fixed") continue;
      if (cs.visibility === "hidden" || cs.display === "none") continue;
      const top = parseFloat(cs.top || "0");
      if (!Number.isFinite(top) || top > 1) continue;
      const r = el.getBoundingClientRect();
      // Only chrome actually pinned at the top of the viewport.
      if (r.top > 1 || r.bottom <= 0) continue;
      if (r.bottom > bottom) bottom = r.bottom;
    }
  } catch {
    bottom = 0;
  }
  // Sanity clamp: never let a full-screen overlay masquerade as a header.
  const vh = window.innerHeight || 800;
  if (bottom > vh * 0.4) bottom = 0;
  cachedHeaderBottom = bottom;
  cachedHeaderWidth = w;
  return bottom;
}

if (typeof window !== "undefined") {
  window.addEventListener("resize", () => {
    cachedHeaderBottom = null;
  });
}

/** The y a scroll target must clear to be genuinely readable. */
export function topBandPx(): number {
  return Math.max(72, stickyHeaderBottom() + 8);
}

function alreadyComfortablyVisible(el: HTMLElement): boolean {
  const r = el.getBoundingClientRect();
  const vh = window.innerHeight || 800;
  const topBand = topBandPx();
  const bottomBand = vh - 140;
  if (r.height >= bottomBand - topBand) {
    // Taller than the band: visible enough when its top is in the band.
    return r.top >= topBand && r.top <= vh * 0.5;
  }
  return r.top >= topBand && r.bottom <= bottomBand;
}

export function easedScrollTo(
  target: HTMLElement | null,
  offsetAboveTarget = 96,
  durationMs = 560,
): void {
  if (!target || typeof window === "undefined") return;
  if (editableHasFocus()) return;
  // Keyboard-close reflow window: the input blurred moments ago, iOS is
  // still resettling the viewport. A deliberate request (e.g. the sticky
  // bar tap that dismissed the keyboard) is QUEUED to run after the
  // reflow settles rather than dropped; any user input in between bumps
  // the generation and cancels the queued run.
  const sinceBlur = performance.now() - lastEditableBlurAt;
  if (sinceBlur < BLUR_WINDOW_MS) {
    const reserved = ++scrollGeneration;
    window.setTimeout(() => {
      if (scrollGeneration !== reserved) return;
      easedScrollTo(target, offsetAboveTarget, durationMs);
    }, BLUR_WINDOW_MS - sinceBlur + 30);
    return;
  }
  // A finger is down, or momentum from a just-lifted finger may still be
  // running. The page is the customer's; skip rather than queue.
  if (touchActive) return;
  if (performance.now() - lastTouchEndAt < MOMENTUM_GRACE_MS) return;
  // Already on screen: scrolling to it is motion without information.
  if (alreadyComfortablyVisible(target)) return;
  // In-cart modal: scroll the modal's own scroll host instead of the
  // window (which would move the page BEHIND the modal and leave the
  // next step out of view).
  // 2026-07-25: never land the target underneath the theme's sticky header.
  // The 96px default predates the storefront's 162.5px phone header, so a
  // "successful" scroll could still finish with the step hidden behind it.
  // Only applies to the window scroll; a modal's own scroll host sits below
  // the header already.
  const effectiveOffset = Math.max(offsetAboveTarget, topBandPx());
  const container = findScrollContainer(target);
  if (container) {
    const cTop = container.getBoundingClientRect().top;
    const tTop = target.getBoundingClientRect().top;
    const top = container.scrollTop + (tTop - cTop) - offsetAboveTarget;
    try {
      container.scrollTo({ top: Math.max(0, top), behavior: "smooth" });
    } catch {
      container.scrollTop = Math.max(0, top);
    }
    return;
  }
  const startY = window.scrollY || window.pageYOffset || 0;
  const initialTargetY =
    target.getBoundingClientRect().top + startY - effectiveOffset;
  // Nothing meaningful to scroll — snap and bail.
  if (Math.abs(initialTargetY - startY) < 4) {
    window.scrollTo(0, initialTargetY);
    return;
  }
  // C5: honour prefers-reduced-motion — jump instantly instead of running
  // the eased animation for customers who've asked the OS to reduce motion.
  if (
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    window.scrollTo(0, initialTargetY);
    return;
  }
  // The shared generation counter is the abort mechanism: any user input
  // (touchstart/wheel/keydown, wired at module scope above) bumps it, and so
  // does STARTING a new eased scroll, so two loops can never interleave
  // absolute writes. The old per-invocation `aborted` flag missed both cases.
  const myGeneration = ++scrollGeneration;
  const startTime = performance.now();
  let lastFrameAt = startTime;
  const tick = (now: number) => {
    if (scrollGeneration !== myGeneration) return;
    // Re-check mid-flight: a keyboard can open (autofocus, iOS quirk) after
    // the scroll started.
    if (editableHasFocus()) return;
    const t = Math.min((now - startTime) / durationMs, 1);
    // Anchor each frame to the LIVE position rather than blending from a
    // startY captured before the keyboard closed or the accordion collapsed.
    // The old absolute blend force-overrode the browser's own viewport
    // adjustments frame after frame, which read as the page fighting the
    // customer. This converges on the (also live) target instead: each frame
    // covers the fraction of remaining distance that the easing curve says
    // this slice of time deserves, so external shifts are absorbed rather
    // than overwritten.
    const dtFrac = Math.min((now - lastFrameAt) / durationMs, 1);
    lastFrameAt = now;
    const remainingTime = Math.max(1 - t + dtFrac, dtFrac);
    const frameFraction = Math.min(dtFrac / remainingTime, 1);
    const liveCurrentY = window.scrollY || window.pageYOffset || 0;
    const liveTargetY =
      target.getBoundingClientRect().top + liveCurrentY - effectiveOffset;
    const delta = (liveTargetY - liveCurrentY) * frameFraction;
    if (t >= 1) {
      window.scrollTo(0, Math.max(0, liveTargetY));
      return;
    }
    window.scrollTo(0, Math.max(0, liveCurrentY + delta));
    requestAnimationFrame(tick);
  };
  requestAnimationFrame(tick);
}

// Returns a ref to attach to an accordion element. Whenever `open` flips
// from false to true the page eases to bring that element into view, so
// the customer follows the flow instead of being left behind. No scroll
// fires on the initial mount (an already-open element is the page's
// starting point, not a transition).
export function useOpenScroll<T extends HTMLElement>(open: boolean) {
  const ref = React.useRef<T | null>(null);
  const wasOpen = React.useRef(open);
  React.useEffect(() => {
    if (open && !wasOpen.current && ref.current) {
      // Bring the section's anchor into the upper-middle of the screen.
      easedScrollTo(ref.current, Math.round((window.innerHeight || 800) * 0.4));
    }
    wasOpen.current = open;
  }, [open]);
  return ref;
}
